import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Building2, User, Loader2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const emptyForm = {
  customer_type: 'individual',
  name: '',
  contact_person: '',
  email: '',
  phone_numbers: [''],
  address: '',
  location: null,
  tin_number: ''
};

/* Location picker — searches the map (OpenStreetMap geocoding via our API)
   and stores {address, lat, lon}. The saved point opens in Google Maps. */
function LocationPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const search = (q) => {
    setQuery(q);
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/geo/search`, { ...authHeaders(), params: { q } });
        setResults(res.data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);
  };

  const pick = (r) => {
    onChange(r);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-start gap-2 border rounded-md p-2 bg-green-50">
          <MapPin size={16} className="text-green-700 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <p>{value.address}</p>
            {value.lat && (
              <a className="text-blue-600 text-xs inline-flex items-center gap-1 hover:underline"
                href={`https://www.google.com/maps?q=${value.lat},${value.lon}`} target="_blank" rel="noreferrer">
                Open in Google Maps <ExternalLink size={10} />
              </a>
            )}
          </div>
          <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => onChange(null)}>×</Button>
        </div>
      ) : (
        <div className="relative">
          <Input
            data-testid="location-search-input"
            placeholder="Search location on the map (e.g. Sokoni, Arusha)…"
            value={query}
            onChange={(e) => search(e.target.value)}
          />
          {searching && <Loader2 size={16} className="animate-spin absolute right-3 top-2.5 text-gray-400" />}
          {results.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-52 overflow-y-auto">
              {results.map((r, i) => (
                <button key={i} type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex items-start gap-2"
                  onClick={() => pick(r)}>
                  <MapPin size={14} className="mt-0.5 text-[#EA5829] shrink-0" />
                  <span>{r.address}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-400">Or type the location manually in the Address field below.</p>
    </div>
  );
}

export default function Customers({ user }) {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  // Which fields staff must fill in is configured in Settings — nothing is
  // mandatory unless admin turned it on there.
  const [requiredFields, setRequiredFields] = useState([]);

  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const seesAll = isAdmin || (user?.privileges || []).includes('customers_all');

  useEffect(() => {
    fetchCustomers();
    fetchRequiredFields();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, authHeaders());
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchRequiredFields = async () => {
    try {
      const res = await axios.get(`${API}/settings/customer-fields`, authHeaders());
      setRequiredFields(res.data?.required || []);
    } catch (error) {
      console.error('Failed to load onboarding field settings');
    }
  };

  const isRequired = (field) => requiredFields.includes(field);
  const star = (field) => (isRequired(field) ? ' *' : '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRequired('location') && !formData.location) {
      toast.error('Please pick the customer location on the map');
      return;
    }
    const payload = {
      ...formData,
      phone_numbers: formData.phone_numbers.filter((n) => n.trim()),
      email: formData.email || null,
    };
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, payload, authHeaders());
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, payload, authHeaders());
        toast.success('Customer onboarded successfully');
      }
      setOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, authHeaders());
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingCustomer(null);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_type: customer.customer_type || (customer.company_name ? 'company' : 'individual'),
      name: customer.name || '',
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone_numbers: customer.phone_numbers?.length ? customer.phone_numbers : [customer.phone || ''],
      address: customer.address || '',
      location: customer.location || null,
      tin_number: customer.tin_number || ''
    });
    setOpen(true);
  };

  const setPhone = (i, v) => {
    const numbers = [...formData.phone_numbers];
    numbers[i] = v;
    setFormData({ ...formData, phone_numbers: numbers });
  };

  const isCompany = formData.customer_type === 'company';

  return (
    <div data-testid="customers-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Customers</h1>
          <p className="text-slate-600 mt-1">
            {seesAll ? 'All onboarded customers' : 'Customers you have onboarded'}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Onboard Customer
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="customer-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Onboard New Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Customer Type *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="type-individual"
                    className={`flex items-center justify-center gap-2 border rounded-md py-2 text-sm font-medium ${!isCompany ? 'border-[#EA5829] bg-orange-50 text-[#EA5829]' : 'text-gray-500'}`}
                    onClick={() => setFormData({ ...formData, customer_type: 'individual' })}>
                    <User size={16} /> Individual
                  </button>
                  <button type="button" data-testid="type-company"
                    className={`flex items-center justify-center gap-2 border rounded-md py-2 text-sm font-medium ${isCompany ? 'border-[#EA5829] bg-orange-50 text-[#EA5829]' : 'text-gray-500'}`}
                    onClick={() => setFormData({ ...formData, customer_type: 'company' })}>
                    <Building2 size={16} /> Organization / Company
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">{(isCompany ? 'Company Name' : 'Full Name') + star('name')}</Label>
                <Input id="name" data-testid="customer-name-input" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} required={isRequired('name')} />
              </div>
              {isCompany && (
                <div className="space-y-2">
                  <Label htmlFor="contact_person">{`Contact Person Name${star('contact_person')}`}</Label>
                  <Input id="contact_person" data-testid="customer-contact-input" value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} required={isRequired('contact_person')} />
                </div>
              )}
              <div className="space-y-2">
                <Label>{`Phone Numbers${star('phone_numbers')}`}</Label>
                {formData.phone_numbers.map((n, i) => (
                  <div key={i} className="flex gap-2">
                    <Input data-testid={`customer-phone-input-${i}`} placeholder="+255 7XX XXX XXX" value={n}
                      onChange={(e) => setPhone(i, e.target.value)} required={isRequired('phone_numbers') && i === 0} />
                    {formData.phone_numbers.length > 1 && (
                      <Button type="button" variant="outline" size="sm" className="text-red-600"
                        onClick={() => setFormData({ ...formData, phone_numbers: formData.phone_numbers.filter((_, j) => j !== i) })}>×</Button>
                    )}
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline"
                  onClick={() => setFormData({ ...formData, phone_numbers: [...formData.phone_numbers, ''] })}>
                  + Add another number
                </Button>
              </div>
              <div className="space-y-2">
                <Label>{`Location${star('location')}`}</Label>
                <LocationPicker value={formData.location}
                  onChange={(loc) => setFormData({ ...formData, location: loc, address: loc?.address || formData.address })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{`Address / Location Description${star('address')}`}</Label>
                <Input id="address" data-testid="customer-address-input" value={formData.address}
                  placeholder="e.g. Sokoni Street, opposite bus stand"
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} required={isRequired('address')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{`Email${star('email')}`}</Label>
                <Input id="email" data-testid="customer-email-input" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required={isRequired('email')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tin_number">{`TIN Number${star('tin_number')}`}</Label>
                <Input id="tin_number" data-testid="customer-tin-input" value={formData.tin_number}
                  onChange={(e) => setFormData({ ...formData, tin_number: e.target.value })} required={isRequired('tin_number')} />
              </div>
              <Button data-testid="save-customer-button" type="submit" className="w-full">
                {editingCustomer ? 'Update Customer' : 'Onboard Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-4 max-w-xs">
        <CardContent className="pt-4">
          <p className="text-xs text-gray-500">{seesAll ? 'Total customers' : 'Customers you onboarded'}</p>
          <p className="text-2xl font-bold text-[#EA5829]" data-testid="customer-count">{customers.length}</p>
        </CardContent>
      </Card>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead>Phone(s)</TableHead>
              <TableHead>Location</TableHead>
              {seesAll && <TableHead>Onboarded By</TableHead>}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={seesAll ? 7 : 6} className="text-center text-slate-500">
                  No customers yet. Onboard your first customer.
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                  <TableCell>
                    <Badge variant="outline" className={customer.customer_type === 'company' ? 'bg-blue-50 text-blue-700' : 'bg-slate-50'}>
                      {customer.customer_type === 'company' ? 'Company' : 'Individual'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{customer.name || <span className="text-slate-400">(no name)</span>}</TableCell>
                  <TableCell>{customer.contact_person || '-'}</TableCell>
                  <TableCell className="text-sm">
                    {(customer.phone_numbers?.length ? customer.phone_numbers : [customer.phone]).filter(Boolean).join(', ')}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1">
                      {customer.location?.lat ? (
                        <a className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          href={`https://www.google.com/maps?q=${customer.location.lat},${customer.location.lon}`}
                          target="_blank" rel="noreferrer" title="Open in Google Maps">
                          <MapPin size={14} />{(customer.address || customer.location.address || '').slice(0, 40)}
                        </a>
                      ) : (
                        (customer.address || '-').slice(0, 40)
                      )}
                    </span>
                  </TableCell>
                  {seesAll && <TableCell className="text-sm">{customer.onboarded_by_name || '-'}</TableCell>}
                  <TableCell className="text-right">
                    <Button data-testid={`edit-customer-${customer.id}`} variant="ghost" size="icon"
                      onClick={() => openEditDialog(customer)}>
                      <Pencil size={16} />
                    </Button>
                    <Button data-testid={`delete-customer-${customer.id}`} variant="ghost" size="icon"
                      onClick={() => handleDelete(customer.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
